import {
  Flex,
  FlexProps,
  Link,
  LinkProps,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import omit from "lodash/omit";
import { Link as RouterLink } from "react-router";

import { highlightStringMatch } from "@/common/components/Autocomplete/AutocompleteSelect";
import { Avatar, AvatarProps } from "@/common/components/Avatar";
import { useEnvironmentUnsafe } from "@/common/contexts/EnvironmentContext";
import { UserUrl } from "@reflag/shared/urls";
import { UserName } from "@reflag/shared/userAPI";
import { OptionalKeys } from "@reflag/shared/utils/types";
import { resolveUserDisplayName } from "@reflag/shared/utils/userDisplay";

export type UserDisplayProps = {
  user: OptionalKeys<UserName, "name" | "email" | "avatarUrl">;
  search?: string;
  showId?: boolean;
  showEmail?: boolean;
  onlyFirstName?: boolean;
  _avatar?: AvatarProps;
} & (
  | ({ link: true } & Omit<LinkProps, "size">)
  | ({ link?: false | undefined } & FlexProps)
);

export function UserDisplay({
  user,
  search,
  showId,
  showEmail,
  onlyFirstName,
  _avatar,
  ...rest
}: UserDisplayProps) {
  const env = useEnvironmentUnsafe()?.env;
  const fullDisplay = resolveUserDisplayName(user);
  const display = resolveUserDisplayName(user, { onlyFirstName });
  const displayText = display.value;
  const avatarUrl = user.avatarUrl;

  const dimmedColor = useColorModeValue("gray.400", "gray.600");

  const commonFlexProps = {
    display: "flex",
    alignItems: "center",
    fontSize: "sm",
    gap: 1.5,
    title: onlyFirstName ? fullDisplay.value : undefined,
    whiteSpace: "nowrap",
    isTruncated: true,
  };

  const content = (
    <>
      <Avatar
        name={!user.isUnknown ? displayText : undefined}
        src={avatarUrl ?? undefined}
        {..._avatar}
      />
      <Text color={user.isUnknown ? dimmedColor : undefined} isTruncated>
        {search ? highlightStringMatch(displayText, search) : displayText}
      </Text>
      {showEmail && user.email ? (
        <Text isTruncated>
          &lt;
          {search ? highlightStringMatch(user.email, search) : user.email}
          &gt;
        </Text>
      ) : null}
      {showId && display.source !== "id" && user.id ? (
        <Text color={dimmedColor} isTruncated>
          {search ? highlightStringMatch(user.id, search) : user.id}
        </Text>
      ) : null}
    </>
  );

  if (rest.link) {
    if (env) {
      return (
        <Link
          as={RouterLink}
          textDecoration="none"
          to={UserUrl(env, user.id)}
          {...commonFlexProps}
          {...omit(rest, "link")}
        >
          {content}
        </Link>
      );
    }

    return (
      <Flex {...commonFlexProps} {...(omit(rest, "link") as FlexProps)}>
        {content}
      </Flex>
    );
  }

  return (
    <Flex {...commonFlexProps} {...rest}>
      {content}
    </Flex>
  );
}
